import React, { useState, useEffect } from "react";
import { Col, Row, Space, Spin } from "antd";
import clsx from "clsx";
import styles from "@aspen/theme/Intelligence.module.less";
import { fecthIntelligenceNews } from "@aspen/services";
import { ONLY_MOBILE, i18nUtil, INTELLIGENCE_PATHS, systemDetection } from "@aspen/libs";
import type { INews, IReqIntelligenceNews } from "@aspen/model";
import { BRAND, EVENT_ACTIONS, NEWSTYPE } from "@aspen/model";
import {
  NewsCard,
  ExpertCardItem,
  DownloadAppCard,
  NewsCardItem,
  ResearchItem,
  EmptyShareDetail,
  DetailInfo,
  JumpApp
} from "@aspen/widgets";
import { HeadRender } from "@aspen/ui";
import { useAsideHeight } from "@aspen/hooks";

interface IProps {
  detailData: INews;
}

const BriefingDetail: React.FC<IProps> = (props: IProps) => {
  const [detailContent, setDetailContent] = useState<INews | null>(props.detailData);
  // 研报
  const [researchList, setResearchList] = useState<INews[]>([]);
  const [researchLoading, setResearchLoading] = useState<boolean>(false);
  // 专栏
  const [expertList, setExpertList] = useState<INews[]>([]);
  const [expertLoading, setExpertLoading] = useState<boolean>(false);
  // 热点追踪
  const [newsList, setNewsList] = useState<INews[]>([]);
  const [newsLoading, setNewsLoading] = useState<boolean>(false);

  const intl = i18nUtil.t();
  const system = systemDetection();
  const recommendedReading = intl["intelligence.research.recommended.reading"];
  const hotNewsText = intl["intelligence.news.hot.news.text"];
  const expertViews = intl["intelligence.expert.views"];
  const moreText = intl["link.more"];

  const { ref: asideRef } = useAsideHeight();

  useEffect(() => {
    if (props.detailData) setDetailContent(props.detailData);
  }, [props.detailData]);

  useEffect(() => {
    if (!ONLY_MOBILE) {
      // 获取研报列表
      getResearchList();
      // 获取专栏列表
      getExpertList();
      // 获取热点追踪
      getNewsList();
    }
  }, []);

  // 获取研报列表
  const getResearchList = () => {
    setResearchLoading(true);
    const params: IReqIntelligenceNews = {
      limit: 4,
      action: "all",
      type: NEWSTYPE.research // 研报
    };
    fecthIntelligenceNews(params)
      .then((res) => {
        if (res?.code == "0") {
          setResearchList(res?.data?.rows ?? []);
        }
      })
      .finally(() => {
        setResearchLoading(false);
      });
  };

  // 获取专栏文章列表
  const getExpertList = () => {
    setExpertLoading(true);
    const params: IReqIntelligenceNews = {
      limit: 3,
      action: "all",
      type: NEWSTYPE.expert // 专栏
    };
    fecthIntelligenceNews(params)
      .then((res) => {
        if (res?.code == "0") {
          setExpertList(res?.data?.rows ?? []);
        }
      })
      .finally(() => {
        setExpertLoading(false);
      });
  };

  // 获取 Hot News List
  const getNewsList = () => {
    setNewsLoading(true);
    const params: IReqIntelligenceNews = {
      limit: 4,
      action: "all",
      type: NEWSTYPE.news // 爬虫新闻
    };
    fecthIntelligenceNews(params)
      .then((res) => {
        if (res?.code == "0") {
          setNewsList(res?.data?.rows ?? []);
        }
      })
      .finally(() => {
        setNewsLoading(false);
      });
  };

  return (
    <>
      {/* 新闻详情单独处理 */}
      <HeadRender
        title={detailContent?.title}
        description={detailContent?.summary}
        image={detailContent?.images?.[0]}
      />

      <section>
        <Row className={styles.row} gutter={16}>
          <Col flex="auto" style={{ width: "calc(100% - 340px)" }}>
            <Spin spinning={researchLoading}>
              <div className={clsx(styles.marketDailyDetail, ONLY_MOBILE && styles.app)}>
                {detailContent ? (
                  <>
                    <DetailInfo type="marketDaily" detailInfo={detailContent} />
                    {ONLY_MOBILE ? (
                      <JumpApp
                        event_action={EVENT_ACTIONS.briefingDailyDetail}
                        id={detailContent?.id}
                        path={INTELLIGENCE_PATHS.INTELLIGENCE_MARKET_DAILY_DETAIL}
                      />
                    ) : null}
                  </>
                ) : (
                  <EmptyShareDetail />
                )}
                {ONLY_MOBILE ||
                  (researchList?.length > 0 && (
                    <NewsCard
                      loading={researchLoading}
                      title={recommendedReading}
                      link={INTELLIGENCE_PATHS.INTELLIGENCE_RESEARCH}>
                      <Row className={styles.readingList} gutter={30}>
                        {researchList.map((item) => (
                          <Col span={12} key={item.id}>
                            <ResearchItem key={item.id} data={item} sourcePage="detail" />
                          </Col>
                        ))}
                      </Row>
                    </NewsCard>
                  ))}
              </div>
            </Spin>
          </Col>
          {!ONLY_MOBILE && (
            <Col
              flex="340px"
              ref={asideRef}
              className={styles.detailExtraContainer}
              style={{
                position: expertLoading || newsLoading ? "static" : "sticky"
              }}>
              <Spin spinning={expertLoading || newsLoading}>
                <Space size={16} direction="vertical">
                  {expertList?.length > 0 && (
                    <NewsCard
                      title={expertViews}
                      linkText={moreText}
                      link={INTELLIGENCE_PATHS.INTELLIGENCE_EXPERT}>
                      {expertList?.map((item) => <ExpertCardItem key={item.id} data={item} />)}
                    </NewsCard>
                  )}
                  <NewsCard
                    title={hotNewsText}
                    linkText={moreText}
                    link={INTELLIGENCE_PATHS.INTELLIGENCE_NEWS}>
                    {newsList?.map((item) => (
                      <NewsCardItem
                        key={item.id}
                        data={item}
                        path={INTELLIGENCE_PATHS.INTELLIGENCE_NEWS_DETAIL}
                      />
                    ))}
                  </NewsCard>
                  {system === BRAND ? <DownloadAppCard /> : null}
                </Space>
              </Spin>
            </Col>
          )}
        </Row>
      </section>
    </>
  );
};

export const PageBriefingDetail = React.memo(BriefingDetail);
